import { Id, Nullable, User } from 'types';
import { Key } from 'antd/lib/table/interface';
import { RcFile } from 'antd/lib/upload';

export enum PricingRoutes {
  ContractList = `/pricing/contract/list`,
  ContractCreate = `/pricing/contract/create`,
  ContractEdit = `/pricing/contract/edit`,
  ContractView = `/pricing/contract`,
}

export interface PricingRouteContractListParams {
  category: string | undefined;
  query: string | undefined;
}

export interface PricingRouteContractEditParams {
  contractId: string;
}

export enum ApiResponseErrorMessage {
  UnprocessableEntity = '422 Unprocessable Entity',
}

export interface Breeder {
  id: Id;
  users: User[];
}

export enum BreederRespError {
  InvalidBreederId = 'Invalid Breeder ID',
}

export interface ContentItemBase {
  id: number;
  name: string;
}

export interface Color extends ContentItemBase {
  colorFamilyId: number;
  requiresHearingTest?: boolean;
}

export interface BreedVariety {
  breedId: number;
  id: number;
  varietyId: number;
}

export type ContractMode = 'create' | 'edit' | 'view';

export interface Contract {
  id: Id;
  breederContractConditionId: number;
  breederContractTerms: ContractTerm[];
  breederContractStatusId: number;
  breederName: string;
  breederId: number;
  createdBy?: string;
  createdByUser?: {
    email: string;
    firstName: string;
    id: number;
    lastName: string;
  };
  createdByUserId: number;
  companyName: string;
  endDate: string;
  startDate: string;
  media: Media[];
  totalQuantity: number;
}

export interface ContractFormValues {
  breederId: number;
  breederName: string;
  breederContractConditionId: number;
  startDate: string;
  endDate: string;
  contractFile: RcFile;
}

export interface ContractsApiResponse {
  currentPage: number;
  data: Contract[];
  firstPageUrl?: string;
  from?: number;
  lastPage?: number;
  lastPageUrl?: Nullable<string>;
  nextPageUrl?: Nullable<string>;
  path?: string;
  perPage?: Nullable<number>;
  prevPageUrl?: Nullable<string>;
  to?: number;
  total: number;
}

export enum MediaTypeId {
  BreederContract = 18,
  BreederContractAmendment = 20,
}

export interface Media {
  id: number;
  name: string;
  dataUrl: string;
  originalFilePath: string;
  filePath: string;
  mediaTypeId: number;
  mediaStatusId: number;
  visibilityId: number;
  uploadedByUserId: number;
  uploadedAt: string;
}

export interface UploadMediaPayload {
  mediaEntityId: Id;
  mediaEntity: string;
  mediaTypeId: number;
  viewOrder: number;
  isDefault: number;
  file: RcFile;
}

export interface AwsS3Signature {
  [key: string]: string;
  'Content-Type': string;
  Key: string;
  Policy: string;
  'X-Amz-Algorithm': string;
  'X-Amz-Credential': string;
  'X-Amz-Date': string;
  'X-Amz-Security-Token': string;
  'X-Amz-Signature': string;
  acl: string;
  bucket: string;
}

export interface Breed extends ContentItemBase {
  slug: string;
  shortName?: string;
}

export interface ContractFilter {
  breederId?: number;
  typeTermId?: number;
  breedId?: number;
  breederContractTermTypeId?: number;
  colorId?: number;
  quantity?: number;
  varietyId?: number;
}

export enum SearchContracts {
  ContractId = 'contract_id',
  BreederId = 'breeder_id',
  BreederName = 'breeder_name',
}

export interface ContractSearch {
  breeder_id?: number;
  breeder_name?: string;
  contract_id?: number;
  breeder_contract_status_id?: number;
}

export interface ContractRequest {
  sort?: SortColumns;
  filters?: ContractFilter;
  search?: ContractSearch;
  pagination?: PaginationContractList;
}

export interface SortColumns {
  order?: string;
  columnKey?: Key;
}

export interface PaginationContractList {
  currentPage: number;
  totalContracts: number;
  pageSize: number | undefined;
}

export interface ContractTermsFormValues {
  breedId: number;
  breederContractTermTypeId: number;
  colorIds: number[];
  confirmSave: boolean;
  quantity: number;
  varietyIds: number[];
  initialCost: number;
  initialPrice: number;
  middleCost: number;
  middlePrice: number;
  finalCost: number;
  finalPrice: number;
}

export interface ContractTerm {
  id: number;
  quantity: number;
  breedId: number;
  colors: ContentItemBase[];
  varieties: ContentItemBase[];
  breederContractTermTypeId: number;
  costs: ContractTermCost[];
}

export interface ContractTermCost {
  id: number;
  breederContractTermCostTierId: number;
  cost: number;
  price: number;
}

export interface ContractTermPayload {
  quantity: number;
  breedId: number;
  colorIds: number[];
  varietyIds: number[];
  breederContractTermTypeId: number;
  costs: ContractTermCostPayload[];
}

export interface ContractTermCostPayload {
  id?: number;
  breederContractTermCostTierId?: number;
  cost: number;
  price: number;
}

export enum ContractCondition {
  Increasing = 1,
  Decreasing = 2,
}

export enum ContractTermCostTierId {
  Initial = 1,
  Increased = 2,
  Decreased = 3,
  Final = 4,
}

export enum ContractStatusId {
  Active = 1,
  Created = 2,
  Discharged = 3,
  Terminated = 4,
}

export interface Breeds {
  id: Id;
  name: string;
  slug: string;
}

export interface PuppyColor extends ContentItemBase {
  colorFamilyId: Id;
}

export type FieldCurrencyTypes = 'Cost' | 'Price';
